import { memo } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

/**
 * SearchPagination component for page-based navigation of search results
 * 
 * @param {Object} props
 * @param {Object} props.searchResults - Search results data
 * @param {Function} props.onPageChange - Handler for page changes
 * @param {Function} props.onNextPage - Handler for next page
 * @param {Function} props.onPrevPage - Handler for previous page
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element}
 */
const SearchPagination = memo(({
  searchResults,
  onPageChange,
  onNextPage,
  onPrevPage,
  className = ''
}) => {
  if (!searchResults || searchResults.totalPages <= 1) {
    return null;
  }

  const { currentPage, totalPages, totalHits, hasNextPage, hasPrevPage } = searchResults;
  const startItem = currentPage * 20 + 1;
  const endItem = Math.min((currentPage + 1) * 20, totalHits);

  // Generate page numbers to show (max 7 pages including current)
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show smart pagination
      const sidePages = 2; // Pages to show on each side of current
      let startPage = Math.max(0, currentPage - sidePages);
      let endPage = Math.min(totalPages - 1, currentPage + sidePages);
      
      // Adjust if we're near the beginning or end
      if (currentPage < sidePages) {
        endPage = Math.min(totalPages - 1, maxVisiblePages - 1);
      } else if (currentPage >= totalPages - sidePages) {
        startPage = Math.max(0, totalPages - maxVisiblePages);
      }
      
      // Always show first page
      if (startPage > 0) {
        pages.push(0);
        if (startPage > 1) {
          pages.push('ellipsis-start');
        }
      }
      
      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      // Always show last page
      if (endPage < totalPages - 1) {
        if (endPage < totalPages - 2) {
          pages.push('ellipsis-end');
        }
        pages.push(totalPages - 1);
      }
    }
    
    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className={`flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-6 ${className}`}>
      {/* Results Info */}
      <div className="text-sm text-gray-700 dark:text-gray-200">
        Showing {startItem.toLocaleString()} to {endItem.toLocaleString()} of{' '}
        {totalHits.toLocaleString()} search results
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center space-x-2">
        {/* Previous Button */}
        <button
          onClick={onPrevPage}
          disabled={!hasPrevPage}
          className={`
            flex items-center px-3 py-2 text-sm font-medium rounded-md border
            focus:outline-none focus:ring-2 focus:ring-obn-blue focus:ring-offset-1
            transition-colors duration-200
            ${hasPrevPage
              ? 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
              : 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-900 cursor-not-allowed'
            }
          `}
          aria-label="Previous page"
        >
          <ChevronLeftIcon className="h-4 w-4 mr-1" />
          Previous
        </button>

        {/* Page Numbers */}
        <div className="hidden sm:flex items-center space-x-1">
          {pageNumbers.map((page, index) => {
            if (typeof page === 'string' && page.startsWith('ellipsis')) {
              return (
                <span key={page} className="px-2 py-1 text-gray-400 dark:text-gray-500">
                  ...
                </span>
              );
            }

            const isCurrentPage = page === currentPage;
            return (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`
                  px-3 py-2 text-sm font-medium rounded-md
                  focus:outline-none focus:ring-2 focus:ring-obn-blue focus:ring-offset-1
                  transition-colors duration-200
                  ${isCurrentPage
                    ? 'bg-obn-blue text-white border border-obn-blue'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }
                `}
                aria-label={`Go to page ${page + 1}`}
                aria-current={isCurrentPage ? 'page' : undefined}
              >
                {page + 1}
              </button>
            );
          })}
        </div>

        {/* Mobile Page Info */}
        <div className="sm:hidden text-sm text-gray-700 dark:text-gray-200">
          Page {currentPage + 1} of {totalPages}
        </div>

        {/* Next Button */}
        <button
          onClick={onNextPage}
          disabled={!hasNextPage}
          className={`
            flex items-center px-3 py-2 text-sm font-medium rounded-md border
            focus:outline-none focus:ring-2 focus:ring-obn-blue focus:ring-offset-1
            transition-colors duration-200
            ${hasNextPage
              ? 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
              : 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-900 cursor-not-allowed'
            }
          `}
          aria-label="Next page"
        >
          Next
          <ChevronRightIcon className="h-4 w-4 ml-1" />
        </button>
      </div>
    </div>
  );
});

SearchPagination.displayName = 'SearchPagination';

export default SearchPagination;
